import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import ProfileHeader from "@/components/profile/ProfileHeader";
import EarnedBadges from "@/components/profile/EarnedBadges";
import SetupChecklist from "@/components/onboarding/SetupChecklist";
import ProfileCompletionCard from "@/components/profile/ProfileCompletionCard";
import MyIdeasTab from "@/components/profile/MyIdeasTab";
import SupportingTab from "@/components/profile/SupportingTab";
import SettingsTab from "@/components/profile/SettingsTab";
import CommentsTab from "@/components/profile/CommentsTab";
import SavedOpportunitiesTab from "@/components/profile/SavedOpportunitiesTab";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

const Profile = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "ideas";
  const [profile, setProfile] = useState<any>(null);
  const [ideaCount, setIdeaCount] = useState<number | null>(null);
  const [supportingCount, setSupportingCount] = useState<number | null>(null);
  const [commentCount, setCommentCount] = useState<number | null>(null);
  const [savedCount, setSavedCount] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    }
  }, [user, loading, navigate]);

  const loadProfile = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();
    setProfile(data);
  }, [user]);

  const loadCounts = useCallback(async () => {
    if (!user) return;
    const [ideasRes, votesRes, commentsRes, savedRes] = await Promise.all([
      supabase
        .from("ideas")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id),
      supabase
        .from("idea_votes")
        .select("idea_id", { count: "exact", head: true })
        .eq("user_id", user.id),
      supabase
        .from("idea_comments")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id),
      supabase
        .from("saved_opportunities")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id),
    ]);
    setIdeaCount(ideasRes.count ?? 0);
    setSupportingCount(votesRes.count ?? 0);
    setCommentCount(commentsRes.count ?? 0);
    setSavedCount(savedRes.count ?? 0);
  }, [user]);

  useEffect(() => {
    loadProfile();
    loadCounts();
  }, [loadProfile, loadCounts, refreshKey]);

  useEffect(() => {
    document.title = "My Profile | emrkt.co";
  }, []);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="container mx-auto max-w-5xl px-6 pt-32 pb-16">
        <div className="mb-6 grid gap-4 md:grid-cols-2">
          <ProfileCompletionCard profile={profile} />
          <SetupChecklist />
        </div>
        <ProfileHeader
          displayName={profile?.display_name ?? ""}
          email={user.email ?? ""}
          avatarUrl={profile?.avatar_url}
          bio={profile?.bio}
          organization={profile?.organization}
          role={profile?.role}
          location={profile?.location}
          ideaCount={ideaCount ?? 0}
          supportingCount={supportingCount ?? 0}
          commentCount={commentCount ?? 0}
          savedCount={savedCount ?? 0}
          profile={profile}
        />
        <EarnedBadges userId={user.id} />

        <Tabs
          value={initialTab}
          onValueChange={(v) => {
            const next = new URLSearchParams(searchParams);
            if (v === "ideas") next.delete("tab");
            else next.set("tab", v);
            setSearchParams(next, { replace: true });
          }}
          className="mt-8"
        >
          <TabsList className="grid w-full grid-cols-5 sm:w-auto sm:inline-grid">
            <TabsTrigger value="ideas">Projects</TabsTrigger>
            <TabsTrigger value="supporting">Supporting</TabsTrigger>
            <TabsTrigger value="comments">Comments</TabsTrigger>
            <TabsTrigger value="saved">Saved</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="ideas" className="mt-6">
            <MyIdeasTab userId={user.id} onCountChange={setIdeaCount} onSupportChange={loadCounts} />
          </TabsContent>
          <TabsContent value="supporting" className="mt-6">
            <SupportingTab userId={user.id} onCountChange={setSupportingCount} />
          </TabsContent>
          <TabsContent value="comments" className="mt-6">
            <CommentsTab userId={user.id} onCountChange={setCommentCount} />
          </TabsContent>
          <TabsContent value="saved" className="mt-6">
            <SavedOpportunitiesTab onCountChange={setSavedCount} />
          </TabsContent>
          <TabsContent value="settings" className="mt-6">
            <SettingsTab
              userId={user.id}
              email={user.email ?? ""}
              onProfileUpdated={() => setRefreshKey((k) => k + 1)}
            />
          </TabsContent>
        </Tabs>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Profile;
